/**
 * Cognitive-diversity persona orchestrator: deterministically synthesises a
 * conflicting council (1 moderator + 3..5 specialists) from a query string.
 * Invariants: pure function, no I/O, no external uuid lib; same input ⇒ identical output.
 */
import type {
  AgentRole,
  DebateTopology,
  InformationAsymmetry,
  Persona,
} from "./types";

export interface GeneratePersonasOptions {
  topology: DebateTopology;
  size?: number;
}

export interface GeneratePersonasResult {
  personas: Persona[];
  seatOrder: string[];
}

/* ---------- deterministic hashing (FNV-1a 32-bit, then expanded to UUID v4-shape) ---------- */

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

function fnv1a(input: string): number {
  let h = FNV_OFFSET;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

function hex(n: number, width: number): string {
  return (n >>> 0).toString(16).padStart(width, "0").slice(-width);
}

/** Deterministic UUID-v4-shape from a seed. NOT cryptographically random — by design. */
function deterministicUuid(seed: string): string {
  const a = fnv1a(seed);
  const b = fnv1a(seed + ":b");
  const c = fnv1a(seed + ":c");
  const d = fnv1a(seed + ":d");
  const segA = hex(a, 8);
  const segB = hex(b & 0xffff, 4);
  const segC = ("4" + hex(b >>> 16, 4)).slice(0, 4);
  const segD = (((c & 0x3fff) | 0x8000).toString(16).padStart(4, "0")).slice(0, 4);
  const segE = (hex(c >>> 16, 4) + hex(d, 8)).slice(0, 12);
  return `${segA}-${segB}-${segC}-${segD}-${segE}`;
}

/* ---------- specialist registry ---------- */

interface SpecialistTemplate {
  role: AgentRole;
  displayName: string;
  systemInstruction: (query: string) => string;
  asymmetry: (query: string) => InformationAsymmetry;
  voiceId: string;
}

// İş Kolu B_extra5 — bu şablonlar backend'in `domain/personas.py`
// (Hızlı Başlat fallback) ile AYNI sorunu taşıyordu: sabit savunma
// sanayii jargonu (ITAR/EAR/STANAG/MIL-STD). `usePreviewedCouncil`
// (canlı önizleme) ve `sessionSlice` fallback yolu üzerinden HER
// kullanıcıya, konu ne olursa olsun, savunma temalı önizleme
// gösteriyordu. Backend'deki nötrleştirmenin (prompt_defaults.py ::
// fallback_persona.*) aynısı burada da uygulanmıştır — domain'e göre
// uyarlanan, savunmaya kilitli olmayan talimatlar.
const SPECIALISTS: SpecialistTemplate[] = [
  {
    role: "strategic-visionary",
    displayName: "Stratejik Vizyoner",
    voiceId: "voice-visionary-01",
    systemInstruction: (q) =>
      `Sen Stratejik Vizyoner'sin. Odağın: uzun vadeli yol haritası, konumlanma ve paradigma değişimleri — sorunun kendi domain'inde. ` +
      `"${q}" konusunda, mevcut statükoyu sorgula, gelecek 10 yıllık trendi analiz et ve ` +
      `rakiplerin/emsallerin stratejik hamleleriyle karşılaştırmalı pozisyon al.`,
    asymmetry: (q) => ({
      sources: [`stratejik-analiz:${fnv1a(q + ":strat").toString(16)}`, "sektor-yol-haritasi:2026-2035", "pazar-istihbarati:Q2"],
      blindSpots: ["kısa vadeli operasyonel kısıtlar", "bütçe gerçekçiliği"],
      confidenceCeiling: 0.78,
    }),
  },
  {
    role: "technical-authority",
    displayName: "Teknik Otorite",
    voiceId: "voice-techauth-02",
    systemInstruction: (q) =>
      `Sen Teknik Otorite'sin. Odağın: derinlemesine uygulama bilgisi, sistem/süreç entegrasyonu, fizibilite — sorunun kendi domain'inde. ` +
      `"${q}" konusunda, işletim/bakım maliyeti, dış kaynak bağımlılığı, entegrasyon borcu ve ` +
      `ekibin yetkinlik haritasını değerlendir. 3-5 yıllık TCO'yu göz ardı eden öneriyi reddet.`,
    asymmetry: (q) => ({
      sources: [`teknik-dosya:${fnv1a(q + ":tech").toString(16)}`, "saha-raporlari:2023-2026", "entegrasyon-testleri:internal"],
      blindSpots: ["stratejik vizyon", "akademik frontier"],
      confidenceCeiling: 0.86,
    }),
  },
  {
    role: "threat-analyst",
    displayName: "Tehdit ve Risk Analisti",
    voiceId: "voice-threat-03",
    systemInstruction: (q) =>
      `Sen Tehdit ve Risk Analisti'sin. Odağın: operasyonel risk, kaynak/tedarik güvenliği, uyum gereksinimleri — sorunun kendi domain'inde. ` +
      `"${q}" konusunda, olası arıza senaryolarını, etki yarıçapını ve sorunun domain'ine ` +
      `uygulanabilir düzenleyici/uyum çerçevelerini sırala (hangileri geçerliyse — sektöre göre değişir, uydurma).`,
    asymmetry: (q) => ({
      sources: ["risk-kaydi:internal", `olay-raporlari:${fnv1a(q + ":inc").toString(16)}`, "duzenleyici-sirkulerler"],
      blindSpots: ["hızlı ilerleme ihtiyacı", "maliyet optimizasyonu"],
      confidenceCeiling: 0.72,
    }),
  },
  {
    role: "contrarian",
    displayName: "Muhalif Ses",
    voiceId: "voice-contrarian-04",
    systemInstruction: (q) =>
      `Sen Muhalif Ses'sin. Görevin: yapısal kontra-pozisyon, kırmızı takım perspektifi. ` +
      `"${q}" konusunda, önceki konuşmacının iddiasını tersine çevir; kimse konuşmadıysa ` +
      `sorunun çerçevelenişini sorgula. En güçlü karşı-argümanı çürütmeden ASLA onaylama.`,
    asymmetry: () => ({
      sources: ["karsi-olgusal-analiz:synthetic", "kirmizi-takim-dosyasi"],
      blindSpots: ["uzlaşı inşası", "diplomatik ton"],
      confidenceCeiling: 0.65,
    }),
  },
  {
    role: "operational-commander",
    displayName: "Operasyonel Yönetici",
    voiceId: "voice-opscom-05",
    systemInstruction: (q) =>
      `Sen Operasyonel Yönetici'sin. Odağın: saha/uygulama deneyimi, lojistik gerçekçilik, operasyonel sürdürülebilirlik — sorunun kendi domain'inde. ` +
      `"${q}" konusunda, gerçekten uygulanabilir olup olmayacağını, bakım-idame gereksinimlerini, ` +
      `personel eğitim yükünü ve operasyonel tempo uyumunu değerlendir.`,
    asymmetry: (q) => ({
      sources: [`saha-operasyon-raporlari:${fnv1a(q + ":ops").toString(16)}`, "operasyonel-envanter", "egitim-mufredat:2024"],
      blindSpots: ["uzun vadeli teknoloji trendi", "bütçe politikası"],
      confidenceCeiling: 0.81,
    }),
  },
  {
    role: "compliance-officer",
    displayName: "Uyum ve Regülasyon",
    voiceId: "voice-compliance-06",
    systemInstruction: (q) =>
      `Sen Uyum ve Regülasyon Uzmanı'sın. Odağın: ilgili mevzuat uyumu, sertifikasyon/onay gereksinimleri ve standartlar — sorunun kendi domain'inde. ` +
      `"${q}" konusunda, sorunun domain'ine uygulanabilir düzenleyici çerçeveleri, ` +
      `veri koruma yükümlülüklerini (varsa KVKK/GDPR) ve ilgili mevzuat uyumunu denetle.`,
    asymmetry: () => ({
      sources: ["mevzuat-taramasi:internal", "uyum-cercevesi-kaydi:2026", "duzenleyici-liste:internal"],
      blindSpots: ["operasyonel hız", "maliyet optimizasyonu"],
      confidenceCeiling: 0.7,
    }),
  },
];

const MODERATOR_TEMPLATE: SpecialistTemplate = {
  role: "moderator",
  displayName: "Moderatör",
  voiceId: "voice-moderator-00",
  systemInstruction: (q) =>
    `Sen Moderatör'sün. Pozisyon ALMIYORSUN. "${q}" konusunda, kritik-önce disiplinini ` +
    `uygula, ihmal edilen perspektifleri gündeme getir ve scalarScore durağanlaştığında ` +
    `deadlock çağır. Konuştuğunda YALNIZCA gerilimleri özetle, ASLA taraf tutma.`,
  asymmetry: () => ({
    sources: ["tartisma-protokolu:v1", "moderator-kilavuzu:internal"],
    blindSpots: ["alan savunuculuğu"],
    confidenceCeiling: 0.95,
  }),
};

/* ---------- diversity selection ---------- */

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Pick `count` specialists from the pool maximising deterministic stance variance for the seed. */
function pickSpecialists(seed: string, count: number): SpecialistTemplate[] {
  const indexed = SPECIALISTS.map((s, i) => ({
    s,
    score: fnv1a(`${seed}:${s.role}:${i}`),
  }));
  indexed.sort((a, b) => a.score - b.score);
  const picked = indexed.slice(0, count).map((x) => x.s);
  if (!picked.some((p) => p.role === "contrarian")) {
    const contrarian = SPECIALISTS.find((s) => s.role === "contrarian");
    if (contrarian) picked[picked.length - 1] = contrarian;
  }
  return picked;
}

function themeColorFor(seatIndex: number, totalSeats: number): string {
  const hue = Math.round((seatIndex / Math.max(1, totalSeats)) * 360);
  return `hsl(${hue} 72% 56%)`;
}

export function generatePersonas(
  query: string,
  opts?: GeneratePersonasOptions,
): GeneratePersonasResult {
  const topology: DebateTopology = opts?.topology ?? "round-robin";
  const requested = opts?.size ?? 4;
  const specialistCount = clamp(requested, 3, 5);
  const seed = `${query.trim().toLowerCase()}::${topology}`;

  const moderator: Persona = {
    id: deterministicUuid(`${seed}::moderator`),
    role: MODERATOR_TEMPLATE.role,
    displayName: MODERATOR_TEMPLATE.displayName,
    systemInstruction: MODERATOR_TEMPLATE.systemInstruction(query),
    asymmetry: MODERATOR_TEMPLATE.asymmetry(query),
    voiceId: MODERATOR_TEMPLATE.voiceId,
    themeColor: "hsl(220 14% 80%)",
    seatIndex: 0,
  };

  const specialists = pickSpecialists(seed, specialistCount).map(
    (template, i): Persona => {
      const seatIndex = i + 1;
      return {
        id: deterministicUuid(`${seed}::${template.role}`),
        role: template.role,
        displayName: template.displayName,
        systemInstruction: template.systemInstruction(query),
        asymmetry: template.asymmetry(query),
        voiceId: template.voiceId,
        themeColor: themeColorFor(seatIndex, specialistCount + 1),
        seatIndex,
      };
    },
  );

  const personas = [moderator, ...specialists];
  const seatOrder = personas
    .slice()
    .sort((a, b) => a.seatIndex - b.seatIndex)
    .map((p) => p.id);

  return { personas, seatOrder };
}
